import React, { createContext, useContext, useRef, useState, useEffect } from 'react';
import { useSharedData } from './SharedDataContext';

export interface DisplayNamesContextType {
  getDisplayName: (fileId: string) => string;
  setDisplayName: (fileId: string, displayName: string) => void;
  getCustomDisplayName: (fileId: string) => string;
  hasCustomDisplayName: (fileId: string) => boolean;
  clearDisplayName: (fileId: string) => void;
  migrateDisplayNamesFromTrackNumbers: (files: Array<{ id: string }>) => void;
  customDisplayNames: Record<string, string>;
}

const DisplayNamesContext = createContext<DisplayNamesContextType | undefined>(undefined);

// Create a persistent object to store custom display names by file ID
const persistentDisplayNames: Record<string, string> = {};

export const DisplayNamesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Use ref to maintain the same object reference
  const displayNamesRef = useRef(persistentDisplayNames);
  const [, forceUpdate] = useState({});
  const { shareData } = useSharedData();

  // Load display names from shared data when available
  // Shared data now has display names keyed by track position ("1", "2", "3")
  // which matches track.id. We'll store them with track- prefix for migration.
  // Legacy shared data might have file IDs as keys, which we'll handle in migration.
  useEffect(() => {
    if (shareData?.displayNames) {
      console.log('[DisplayNames] Loading display names from shareData:', shareData.displayNames);
      // Clear existing display names before loading new ones
      Object.keys(displayNamesRef.current).forEach(key => {
        delete displayNamesRef.current[key];
      });
      
      // Store shared display names
      // New format: keys are track positions ("1", "2", "3") matching track.id
      // Legacy format: keys might be file IDs - migration will handle by position
      Object.entries(shareData.displayNames).forEach(([key, value]) => {
        // Check if key is a simple number (track position format)
        if (/^\d+$/.test(key)) {
          // Store with track- prefix - will be migrated when files load
          displayNamesRef.current[`track-${key}`] = value as string;
          console.log(`[DisplayNames] Stored track-${key}: "${value}"`);
        } else {
          // Key looks like a file ID (legacy format)
          // Store with shared-fileid prefix - migration will map by position
          displayNamesRef.current[`shared-fileid-${key}`] = value as string;
          console.log(`[DisplayNames] Stored shared-fileid-${key}: "${value}"`);
        }
      });
      
      console.log('[DisplayNames] All stored display names:', Object.keys(displayNamesRef.current));
      forceUpdate({}); // Force a re-render
    }
  }, [shareData]);

  const setDisplayName = (fileId: string, displayName: string) => {
    if (displayName.trim() === '') {
      // If empty, remove the custom display name
      delete displayNamesRef.current[fileId];
    } else {
      // Update the persistent display names object
      displayNamesRef.current[fileId] = displayName.trim();
    }
    forceUpdate({}); // Force a re-render
  };

  const getCustomDisplayName = (fileId: string) => {
    return displayNamesRef.current[fileId] || '';
  };

  const hasCustomDisplayName = (fileId: string) => {
    return Boolean(displayNamesRef.current[fileId]);
  };

  const clearDisplayName = (fileId: string) => {
    delete displayNamesRef.current[fileId];
    forceUpdate({}); // Force a re-render
  };

  const getDisplayName = (fileId: string) => {
    // This method will need to be enhanced to get the actual filename
    // For now, it just returns the custom name or empty string
    return displayNamesRef.current[fileId] || '';
  };

  // Migrate display names from track numbers (legacy) or shared data to file IDs
  const migrateDisplayNamesFromTrackNumbers = (files: Array<{ id: string }>) => {
    const migrated = new Set<string>();
    
    console.log('[DisplayNames] Starting migration for', files.length, 'files');
    console.log('[DisplayNames] Available display names:', Object.keys(displayNamesRef.current));
    
    files.forEach((file, index) => {
      // Extract original track.id from file.id
      // File IDs can be:
      // - "shared-{track.id}" (selective loading)
      // - "{track.id}" (normal loading, track.id as string)
      // - Other formats (legacy)
      let trackId: string | null = null;
      
      // Try to extract from "shared-{id}" format (selective loading)
      if (file.id.startsWith('shared-')) {
        trackId = file.id.replace('shared-', '');
        console.log(`[DisplayNames] File ${file.id}: extracted trackId=${trackId}`);
      } else if (/^\d+$/.test(file.id)) {
        // File ID is already just the track.id as a number string
        trackId = file.id;
        console.log(`[DisplayNames] File ${file.id}: trackId=${trackId} (direct)`);
      }
      
      // First, try track number format using the extracted track.id
      if (trackId) {
        const trackKey = `track-${trackId}`;
        const trackDisplayName = displayNamesRef.current[trackKey];
        console.log(`[DisplayNames] Looking for ${trackKey}:`, trackDisplayName ? 'FOUND' : 'NOT FOUND');
        if (trackDisplayName) {
          displayNamesRef.current[file.id] = trackDisplayName;
          delete displayNamesRef.current[trackKey];
          migrated.add(trackKey);
          console.log(`[DisplayNames] Migrated ${trackKey} -> ${file.id}: "${trackDisplayName}"`);
          return; // Found and migrated, move to next file
        }
      }
      
      // Fallback: try track number format using index+1 (for legacy cases)
      const trackNumber = index + 1;
      const trackKey = `track-${trackNumber}`;
      const trackDisplayName = displayNamesRef.current[trackKey];
      if (trackDisplayName) {
        displayNamesRef.current[file.id] = trackDisplayName;
        delete displayNamesRef.current[trackKey];
        migrated.add(trackKey);
        return; // Found and migrated, move to next file
      }
      
      // Second, try shared file ID format (legacy shared data with file IDs as keys)
      // We'll map them by position since files are loaded in the same order as shared tracks
      const sharedFileIdKeys = Object.keys(displayNamesRef.current)
        .filter(key => key.startsWith('shared-fileid-'))
        .sort(); // Sort to ensure consistent ordering
      if (sharedFileIdKeys.length > 0 && index < sharedFileIdKeys.length) {
        const sharedFileIdKey = sharedFileIdKeys[index];
        const sharedFileIdDisplayName = displayNamesRef.current[sharedFileIdKey];
        if (sharedFileIdDisplayName) {
          displayNamesRef.current[file.id] = sharedFileIdDisplayName;
          delete displayNamesRef.current[sharedFileIdKey];
          migrated.add(sharedFileIdKey);
        }
      }
    });
    
    // Clean up any remaining legacy track- keys that weren't migrated
    // But DON'T delete the migrated file IDs (shared-1, shared-2, etc.) - those are the final display names!
    Object.keys(displayNamesRef.current).forEach(key => {
      // Only clean up track-* keys that weren't migrated (they should have been migrated)
      // Don't delete shared-* keys as those are the migrated display names for file IDs
      if (key.startsWith('track-') && !migrated.has(key)) {
        delete displayNamesRef.current[key];
      }
      // Also clean up legacy shared-fileid-* keys that weren't migrated
      if (key.startsWith('shared-fileid-') && !migrated.has(key)) {
        delete displayNamesRef.current[key];
      }
    });
    
    if (migrated.size > 0) {
      console.log('[DisplayNames] Migration complete. Final display names:', Object.keys(displayNamesRef.current));
      forceUpdate({}); // Force a re-render
    }
  };

  const value: DisplayNamesContextType = {
    getDisplayName,
    setDisplayName,
    getCustomDisplayName,
    hasCustomDisplayName,
    clearDisplayName,
    migrateDisplayNamesFromTrackNumbers,
    customDisplayNames: displayNamesRef.current,
  };

  return (
    <DisplayNamesContext.Provider value={value}>
      {children}
    </DisplayNamesContext.Provider>
  );
};

export const useDisplayNames = (): DisplayNamesContextType => {
  const context = useContext(DisplayNamesContext);
  if (!context) {
    throw new Error('useDisplayNames must be used within a DisplayNamesProvider');
  }
  return context;
};
